import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { CONVENIOS_U, PROCEDIMENTOS_U, medicosSorted } from './constants';
import { isFaturamentoPendente, toggleItem } from './helpers';
import { abrirPdf, documentoGuias } from './pdf';

const fetcher = url => api.get(url).then(res => res.data);

function codigoEmissor(medico, funcionario) {
  return (
    (medico && medico.idFunc) || (funcionario && funcionario.codFunc) || 'ICM'
  );
}

export default function AbaGuias({ ativo }) {
  const medico = useSelector(state => state.doctor.profile);
  const { funcionario } = useSelector(state => state.funcionario);
  const hoje = new Date().toISOString().slice(0, 10);
  const [dataInicial, setDataInicial] = useState(hoje);
  const [dataFinal, setDataFinal] = useState(hoje);
  const [medicosSelecionados, setMedicosSelecionados] = useState([]);
  const [procedimentosSelecionados, setProcedimentosSelecionados] = useState(
    []
  );
  const [conveniosSelecionados, setConveniosSelecionados] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [selectedFat, setSelectedFat] = useState(null);
  const [guia, setGuia] = useState('');
  const [valor, setValor] = useState('');

  const swrKey =
    ativo && dataInicial && dataFinal
      ? `fechamento/guia?dataInicial=${dataInicial}&dataFinal=${dataFinal}`
      : null;
  const {
    data: faturamentos,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(swrKey, fetcher, { revalidateOnFocus: false });

  const pendentes = useMemo(() => {
    if (!faturamentos) return [];
    return faturamentos.filter(f => {
      const medicosOk =
        medicosSelecionados.length === 0 ||
        medicosSelecionados.includes(f.CODMEDICO);
      const procedimentoOk =
        procedimentosSelecionados.length === 0 ||
        procedimentosSelecionados.includes(f.CODPROCEDI);
      const convenioOk =
        conveniosSelecionados.length === 0 ||
        conveniosSelecionados.includes(f.CODCONV);
      return medicosOk && procedimentoOk && convenioOk;
    });
  }, [
    faturamentos,
    procedimentosSelecionados,
    conveniosSelecionados,
    medicosSelecionados,
  ]);

  const pendentesGuia = useMemo(() => pendentes.filter(isFaturamentoPendente), [
    pendentes,
  ]);

  const handleEdit = fat => {
    setSelectedFat(fat);
    setGuia(fat.GUIA || '');
    setValor(fat.VLRLAUDO || '');
    setEditModal(true);
  };

  const handleSalvar = async () => {
    if (valor === '' || valor == null) {
      toast.error('Valor é obrigatório.');
      return;
    }

    try {
      await api.put('/faturamento', {
        codFatura: selectedFat.CODFATURA,
        guia,
        valor: parseFloat(valor),
      });
      toast.success('Guia/valor atualizado com sucesso!');
      setEditModal(false);
      mutate();
    } catch (err) {
      toast.error(
        err?.response?.data?.error || 'Erro ao atualizar guia/valor!'
      );
    }
  };

  const handleImprimir = (dados, titulo, somentePendentes) => {
    if (!dados || dados.length === 0) {
      toast.warn('Nenhum dado para gerar listagem!');
      return;
    }

    abrirPdf(
      documentoGuias({
        dados,
        titulo,
        somentePendentes,
        dataInicial,
        dataFinal,
        codFuncEmissor: codigoEmissor(medico, funcionario),
        now: new Date().toLocaleString('pt-BR', { hour12: false }),
      })
    );
  };

  return (
    <>
      <Form className="mb-3 pb-3 border-bottom">
        <Card.Body className="text-center">
          {medicosSorted.map(agenda => (
            <Form.Check
              key={agenda.codigo}
              inline
              label={agenda.medico}
              size="sm"
              checked={medicosSelecionados.includes(agenda.codigo)}
              onChange={e =>
                toggleItem(
                  setMedicosSelecionados,
                  agenda.codigo,
                  e.target.checked
                )
              }
            />
          ))}
        </Card.Body>
        <div className="row g-2 align-items-end">
          <div className="col-md-2 col-6">
            <Form.Label className="fw-semibold mb-1">Data Inicial</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={dataInicial}
              onChange={e => setDataInicial(e.target.value)}
            />
          </div>
          <div className="col-md-2 col-6">
            <Form.Label className="fw-semibold mb-1">Data Final</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={dataFinal}
              onChange={e => setDataFinal(e.target.value)}
            />
          </div>
          <div className="col-md-4 col-12">
            <Form.Label className="fw-semibold mb-1">Procedimentos</Form.Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PROCEDIMENTOS_U.map(p => (
                <Form.Check
                  key={p}
                  inline
                  label={p}
                  type="checkbox"
                  checked={procedimentosSelecionados.includes(p)}
                  onChange={e =>
                    toggleItem(
                      setProcedimentosSelecionados,
                      p,
                      e.target.checked
                    )
                  }
                />
              ))}
            </div>
          </div>
          <div className="col-md-2 col-12">
            <Form.Label className="fw-semibold mb-1">Convênios</Form.Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CONVENIOS_U.map(c => (
                <Form.Check
                  key={c}
                  inline
                  label={c}
                  type="checkbox"
                  checked={conveniosSelecionados.includes(c)}
                  onChange={e =>
                    toggleItem(setConveniosSelecionados, c, e.target.checked)
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </Form>
      <div className="d-flex justify-content-end mb-2" style={{ gap: 8 }}>
        <Button
          variant="dark"
          size="sm"
          disabled={isLoading || isValidating || pendentes.length === 0}
          onClick={() => handleImprimir(pendentes, 'Guias e Convênios', false)}
        >
          {`Imprimir (${pendentes.length})`}
        </Button>
        <Button
          variant="outline-dark"
          size="sm"
          title="Imprime só linhas com valor em branco, nulo ou zerado, ou número da guia em branco"
          disabled={isLoading || isValidating || pendentesGuia.length === 0}
          onClick={() =>
            handleImprimir(pendentesGuia, 'Guias e Convênios — Pendentes', true)
          }
        >
          {`Imprimir Pendentes (${pendentesGuia.length})`}
        </Button>
      </div>
      {error ? (
        <Alert variant="danger">
          {(error.response &&
            error.response.data &&
            error.response.data.error) ||
            'Erro ao carregar dados.'}
        </Alert>
      ) : isLoading || isValidating ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 160 }}
        >
          <Spinner animation="border" role="status" />
        </div>
      ) : pendentes.length === 0 ? (
        <Alert variant="success">
          Nenhum faturamento pendente de guia ou valor.
        </Alert>
      ) : (
        <Table bordered hover size="sm">
          <thead>
            <tr>
              <th>Data Laudo</th>
              <th>Paciente</th>
              <th>Médico</th>
              <th>Procedimento</th>
              <th>Convênio</th>
              <th>Valor</th>
              <th>Guia</th>
              <th>Editar</th>
            </tr>
          </thead>
          <tbody>
            {pendentes.map(fat => (
              <tr key={fat.CODFATURA}>
                <td>{fat.dataFormatada}</td>
                <td>{fat.NOMEPAC}</td>
                <td>{fat.NOMEMED}</td>
                <td>{fat.DESCPROCEDI}</td>
                <td>
                  {fat.SIGLACONV || (
                    <span style={{ color: 'red' }}>Em branco</span>
                  )}
                </td>
                <td>
                  {fat.VLRLAUDO ? (
                    fat.VLRLAUDO.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })
                  ) : (
                    <span style={{ color: 'red' }}>Em branco</span>
                  )}
                </td>
                <td>
                  {fat.GUIA || <span style={{ color: 'red' }}>Em branco</span>}
                </td>
                <td>
                  <Button size="sm" onClick={() => handleEdit(fat)}>
                    Editar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={editModal} onHide={() => setEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Guia/Valor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Guia</Form.Label>
            <Form.Control
              value={guia}
              onChange={e => setGuia(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Valor</Form.Label>
            <Form.Control
              type="number"
              value={valor}
              onChange={e => setValor(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditModal(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSalvar}>Salvar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
